import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Grid,
  Paper,
  Divider,
  Alert,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardMedia,
  InputAdornment,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";

// Color palette based on #614d42
const colors = {
  primary: '#614d42',
  primaryLight: '#8b6f61',
  primaryDark: '#3f332b',
  primaryFade: '#e8e1dd',
  background: '#faf7f5',
  textPrimary: '#2c2420',
  textSecondary: '#6b5b53',
  border: '#d4ccc7',
  success: '#4c6b5f',
  error: '#b55a4a',
};

// Styled components
const StyledDialog = styled(Dialog)({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 20px 60px rgba(0,0,0,0.1)',
    maxWidth: '900px',
    width: '100%',
  }
});

const DialogHeader = styled(DialogTitle)({
  background: colors.primary,
  color: 'white',
  padding: '20px 24px',
  '& h2': {
    fontSize: '1.5rem',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  }
});

const StyledButton = styled(Button)(({ varianttype }) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontSize: '0.95rem',
  padding: '8px 20px',
  ...(varianttype === 'primary' && {
    background: colors.primary,
    color: 'white',
    '&:hover': {
      background: colors.primaryDark,
    }
  }),
  ...(varianttype === 'outline' && {
    borderColor: colors.primary,
    color: colors.primary,
    '&:hover': {
      borderColor: colors.primaryDark,
      background: colors.primaryFade,
    }
  })
}));

const ColorOption = styled(Box)(({ selected }) => ({
  width: 36,
  height: 36,
  borderRadius: '50%',
  cursor: 'pointer',
  border: selected ? `3px solid ${colors.primary}` : '1px solid transparent',
  boxShadow: selected ? `0 0 0 1px ${colors.primary}` : 'none',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    boxShadow: `0 2px 8px rgba(97, 77, 66, 0.2)`,
  }
}));

const SizeBox = styled(Box)(({ selected }) => ({
  minWidth: 45,
  height: 45,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 8,
  cursor: 'pointer',
  backgroundColor: selected ? colors.primary : colors.primaryFade,
  color: selected ? 'white' : colors.textPrimary,
  border: `1px solid ${colors.border}`,
  fontSize: '0.95rem',
  fontWeight: 500,
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: selected ? colors.primaryDark : colors.primaryLight,
    color: 'white',
  }
}));

const ImagePreview = styled(Box)({
  position: 'relative',
  width: 100,
  height: 100,
  borderRadius: 8,
  overflow: 'hidden',
  border: `1px solid ${colors.border}`,
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  '& .delete-btn': {
    position: 'absolute',
    top: 4,
    right: 4,
    background: 'rgba(255,255,255,0.9)',
    width: 24,
    height: 24,
    '&:hover': {
      background: colors.error,
      color: 'white',
    }
  }
});

// const CategoryCard = styled(Paper)(({ selected }) => ({
//   padding: '16px',
//   borderRadius: 12,
//   cursor: 'pointer',
//   border: `2px solid ${selected ? colors.primary : 'transparent'}`,
//   backgroundColor: selected ? `${colors.primary}10` : colors.background,
//   transition: 'all 0.2s ease',
//   textAlign: 'center',
//   '&:hover': {
//     transform: 'translateY(-2px)',
//     boxShadow: '0 4px 12px rgba(97, 77, 66, 0.1)',
//   }
// }));

const ProductForm = ({ open, handleClose, handleSubmit, editData }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [customColor, setCustomColor] = useState("");
  const [customColorCode, setCustomColorCode] = useState("#000000");
  const [availableColors, setAvailableColors] = useState([
    { name: 'Black', code: '#000000' },
    { name: 'White', code: '#FFFFFF' },
    { name: 'Red', code: '#FF0000' },
    { name: 'Blue', code: '#0000FF' },
  ]);


  const handleAddCustomColor = () => {
    if (!customColor.trim()) return;

    const newColor = {
      name: customColor,
      code: customColorCode
    };

    setAvailableColors(prev => [...prev, newColor]);

    setCustomColor("");
  };
  // Categories

  const categories = [

    'Floral',
    'Nature',
    'Contemporary',
    'BeachSide',
    'View All'
  ];
  const subCategories = {
    'Shirt': ['Casual', 'Formal', 'Party'],
    'T-Shirt': ['Polo', 'V-Neck', 'Round Neck', 'Printed'],
  };





  const sizes = ['XS', 'S', 'M', 'L', 'XL', 'XXL'];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    originalPrice: '',
    category: '',
    subCategory: '',
    gender: 'unisex',
    color: [],
    sizes: [],
    brand: '',
    discount: '',
    inStock: true,
    productDetails: [],
  });

  const [mainImage, setMainImage] = useState(null);
  const [mainPreview, setMainPreview] = useState(null);
  const [otherImages, setOtherImages] = useState([]);
  const [otherPreviews, setOtherPreviews] = useState([]);

  useEffect(() => {
    if (editData) {
      setFormData({
        ...editData,
        color: editData.color || [],
        sizes: editData.sizes || [],
        productDetails: editData.productDetails || [],
      });

      if (editData.mainImage) {
        setMainPreview(editData.mainImage);
      }

      if (editData.otherImages) {
        setOtherPreviews(editData.otherImages);
      }
    }
  }, [editData]);


  useEffect(() => {
    if (editData && open) {
      setActiveStep(0);
    }
  }, [editData, open]);



  useEffect(() => {
    if (editData) {
      setMainPreview(editData.mainImage || null);
      setOtherPreviews(editData.otherImages || []);
    }
  }, [editData]);
  const handleChange = (e) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };



  useEffect(() => {
    const { price, originalPrice } = formData;

    if (price && originalPrice) {
      const discount = Math.round(
        ((originalPrice - price) / originalPrice) * 100
      );

      setFormData(prev => ({
        ...prev,
        discount: discount > 0 ? discount : 0
      }));
    }
  }, [formData.price, formData.originalPrice]);



  const handleColorSelect = (colorName) => {
    setFormData(prev => ({
      ...prev,
      color: prev.color.includes(colorName)
        ? prev.color.filter(c => c !== colorName)
        : [...prev.color, colorName]
    }));
  };

  const handleSizeSelect = (sizes) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.includes(sizes)
        ? prev.sizes.filter(s => s !== sizes)
        : [...prev.sizes, sizes]
    }));
  };

  const handleProductDetailAdd = () => {
    setFormData(prev => ({
      ...prev,
      productDetails: [...prev.productDetails, { id: Date.now(), name: '', value: '' }]
    }));
  };

  const handleProductDetailChange = (id, field, value) => {
    setFormData(prev => ({
      ...prev,
      productDetails: prev.productDetails.map(detail =>
        detail.id === id ? { ...detail, [field]: value } : detail
      )
    }));
  };

  const handleProductDetailDelete = (id) => {
    setFormData(prev => ({
      ...prev,
      productDetails: prev.productDetails.filter(detail => detail.id !== id)
    }));
  };

  const handleMainImage = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMainImage(file);
    setMainPreview(URL.createObjectURL(file));
  };

  const handleOtherImages = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 8) {
      setSnackbar({ open: true, message: 'Maximum 5 images allowed', severity: 'error' });
      return;
    }
    setOtherImages(files);
    setOtherPreviews(files.map(file => URL.createObjectURL(file)));
  };

  const removeOtherImage = (index) => {
    setOtherImages(prev => prev.filter((_, i) => i !== index));
    setOtherPreviews(prev => prev.filter((_, i) => i !== index));
  };

  const validateStep = (step) => {
    switch (step) {
      case 0: return formData.name && formData.price && formData.category;
      case 1: return formData.color.length > 0 && mainPreview;
      case 2: return formData.sizes.length > 0;
      default: return true;
    }
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
    } else {
      setSnackbar({
        open: true,
        message: 'Please fill all required fields',
        severity: 'warning'
      });
    }
  };

  const submit = () => {
    if (!validateStep(activeStep)) {
      setSnackbar({ open: true, message: 'Please complete all steps', severity: 'error' });
      return;
    }

    const submissionData = {
      ...formData,
      _id: editData?._id || Date.now().toString(),

      // main image
      mainImage: mainImage || editData?.mainImage || mainPreview,

      // other images
      otherImages: otherImages.length > 0 ? otherImages : editData?.otherImages,
    };

    handleSubmit(submissionData);

    setSnackbar({
      open: true,
      message: 'Product saved successfully!',
      severity: 'success'
    });

    setTimeout(handleClose, 1000);
  };

  const steps = ['Basic Info', 'Media & Colors', 'Sizes & Details'];

  return (
    <>
      <StyledDialog open={open} onClose={handleClose} maxWidth="lg">
        <DialogHeader>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {editData ? 'Edit Product' : 'Add New Product'}
            </Typography>
            <IconButton onClick={handleClose} sx={{ color: 'white' }}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogHeader>

        <DialogContent sx={{ p: 4, mt: 4 }}>
          {/* Stepper */}
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Basic Info */}
          {activeStep === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: colors.primary, mb: 2 }}>
                  Basic Information
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Product Name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Brand"
                  name="brand"
                  value={formData.brand}
                  onChange={handleChange}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  multiline
                  rows={3}
                  variant="outlined"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Price"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  type="number"
                  required
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Original Price"
                  name="originalPrice"
                  value={formData.originalPrice}
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Category *</InputLabel>
                  <Select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    label="Category *"
                  >
                    {categories.map(cat => (
                      <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {formData.category && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sub Category</InputLabel>
                    <Select
                      name="subCategory"
                      value={formData.subCategory}
                      onChange={handleChange}
                      label="Sub Category"
                    >
                      {subCategories[formData.category]?.map(sub => (
                        <MenuItem key={sub} value={sub}>{sub}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}

              {/* <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount %"
                  name="discount"
                  value={formData.discount}
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  size="small"
                  InputProps={{
                    endAdornment: <InputAdornment position="end">%</InputAdornment>
                  }}
                />
              </Grid> */}

              <Grid item xs={12}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Gender</FormLabel>
                  <RadioGroup row name="gender" value={formData.gender} onChange={handleChange}>
                    <FormControlLabel value="men" control={<Radio />} label="Men" />
                    <FormControlLabel value="women" control={<Radio />} label="Women" />
                    <FormControlLabel value="unisex" control={<Radio />} label="Unisex" />
                    <FormControlLabel value="kids" control={<Radio />} label="Kids" />
                  </RadioGroup>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" gap={2}>
                  <Chip
                    label="In Stock"
                    onClick={() => setFormData(prev => ({ ...prev, inStock: true }))}
                    color={formData.inStock ? "success" : "default"}
                    variant={formData.inStock ? "filled" : "outlined"}
                    sx={{ cursor: 'pointer' }}
                  />
                  <Chip
                    label="Out of Stock"
                    onClick={() => setFormData(prev => ({ ...prev, inStock: false }))}
                    color={!formData.inStock ? "error" : "default"}
                    variant={!formData.inStock ? "filled" : "outlined"}
                    sx={{ cursor: 'pointer' }}
                  />
                </Box>
              </Grid>
            </Grid>
          )}

          {/* Step 2: Media & Colors */}
          {activeStep === 1 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: colors.primary, mb: 2 }}>
                  Media & Colors
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: colors.background }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Main Image *
                  </Typography>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 2, borderColor: colors.border }}
                  >
                    Upload Image
                    <input type="file" hidden accept="image/*" onChange={handleMainImage} />
                  </Button>
                  {mainPreview && (
                    <Card sx={{ maxWidth: 200, mx: 'auto' }}>
                      <CardMedia component="img" height="150" image={mainPreview} alt="Preview" />
                    </Card>
                  )}
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 3, bgcolor: colors.background }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Additional Images (Max 5)
                  </Typography>
                  <Button
                    component="label"
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 2, borderColor: colors.border }}
                  >
                    Upload Images
                    <input type="file" hidden multiple accept="image/*" onChange={handleOtherImages} />
                  </Button>

                  <Box display="flex" gap={1} flexWrap="wrap">
                    {otherPreviews.map((preview, index) => (
                      <ImagePreview key={index}>
                        <img src={preview} alt={`Preview ${index + 1}`} />
                        <IconButton
                          size="small"
                          className="delete-btn"
                          onClick={() => removeOtherImage(index)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ImagePreview>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: colors.background }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Colors *
                  </Typography>

                  <Box display="flex" gap={2} flexWrap="wrap">
                    {availableColors.map(color => (
                      <ColorOption
                        key={color.name}
                        sx={{ bgcolor: color.code }}
                        selected={formData.color.includes(color.name)}
                        onClick={() => handleColorSelect(color.name)}
                      />
                    ))}
                  </Box>

                  {formData.color.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="body2" color="textSecondary">
                        Selected: {formData.color.join(', ')}
                      </Typography>
                    </Box>
                  )}

                  {/* 🔥 CUSTOM COLOR ADD SECTION */}
                  <Box mt={3} display="flex" gap={2} alignItems="center">
                    <TextField
                      label="Custom Color Name"
                      size="small"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      placeholder="e.g. Olive Green"
                    />

                    <input
                      type="color"
                      value={customColorCode}
                      onChange={(e) => setCustomColorCode(e.target.value)}
                      style={{
                        width: 40,
                        height: 40,
                        border: "none",
                        cursor: "pointer"
                      }}
                    />

                    <Button
                      variant="contained"
                      onClick={handleAddCustomColor}
                    >
                      Add
                    </Button>
                  </Box>

                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Step 3: Sizes & Details */}
          {activeStep === 2 && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: colors.primary, mb: 2 }}>
                  Sizes & Specifications
                </Typography>
                <Divider />
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: colors.background }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Available Sizes *
                  </Typography>
                  <Box display="flex" gap={1} flexWrap="wrap">
                    {sizes.map(size => (
                      <SizeBox
                        key={size}
                        selected={formData.sizes.includes(size)}
                        onClick={() => handleSizeSelect(size)}
                      >
                        {size}
                      </SizeBox>
                    ))}
                  </Box>
                </Paper>
              </Grid>

              <Grid item xs={12}>
                <Paper sx={{ p: 3, bgcolor: colors.background }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                    <Typography variant="subtitle2">Product Specifications</Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={handleProductDetailAdd}
                      sx={{ borderColor: colors.primary, color: colors.primary }}
                    >
                      Add
                    </Button>
                  </Box>

                  {formData.productDetails.map((detail, index) => (
                    <Paper key={detail.id} sx={{ p: 2, mb: 2, bgcolor: 'white' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Name"
                            value={detail.name}
                            onChange={(e) => handleProductDetailChange(detail.id, 'name', e.target.value)}
                            placeholder="e.g., Fabric"
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            fullWidth
                            size="small"
                            label="Value"
                            value={detail.value}
                            onChange={(e) => handleProductDetailChange(detail.id, 'value', e.target.value)}
                            placeholder="e.g., Cotton"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton color="error" onClick={() => handleProductDetailDelete(detail.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}

                  {formData.productDetails.length === 0 && (
                    <Typography color="textSecondary" textAlign="center" py={3}>
                      No specifications added
                    </Typography>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}

          {/* Navigation */}
          <Box display="flex" justifyContent="space-between" mt={4}>
            <StyledButton
              varianttype="outline"
              variant="outlined"
              onClick={() => setActiveStep(prev => Math.max(0, prev - 1))}
              disabled={activeStep === 0}
              startIcon={<ArrowBackIcon />}
            >
              Previous
            </StyledButton>

            {activeStep === 2 ? (
              <StyledButton
                varianttype="primary"
                variant="contained"
                onClick={submit}
                startIcon={<SaveIcon />}
              >
                {editData ? 'Update' : 'Create'} Product
              </StyledButton>
            ) : (
              <StyledButton
                varianttype="primary"
                variant="contained"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
              >
                Next
              </StyledButton>
            )}
          </Box>
        </DialogContent>
      </StyledDialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ProductForm;